import React, {useEffect, useState} from 'react'
import db from '../../../../../services/firebase'
import {
  collection,
  doc,
  onSnapshot,
  updateDoc,
  addDoc,
  getDoc,
  serverTimestamp,
} from 'firebase/firestore'
import {useAuth} from '../../../../modules/auth'
import {ExportVendor} from '../utils/ExportVendor'
import {ExportAsset} from '../utils/ExportAsset'
import {ExportRisk} from '../utils/ExportRisk'
import {ExportTest} from '../utils/ExportTest'
import {ExportControl} from '../utils/ExportControl'
import {ExportEmployee} from '../utils/ExportEmployees'
import {ExportZipPolicy} from '../utils/ExportZipPolicy'
import {ExportZipTest} from '../utils/ExportZipTest'
import Drawer from 'react-modern-drawer'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import {Link} from 'react-router-dom'
import Input from 'app/components/Input'
import DrawerHeader from 'app/components/Drawer/DrawerHeader'

const AddAudit = ({isOpen, onClose}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])
  const {currentUser} = useAuth()
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [frameworks, setFrameworks] = useState<string[]>([])
  const [owner, setOwner] = useState('')
  const [owners, setOwners] = useState<any[]>([])
  const [organization, setOrganization] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [selectedAuditType, setSelectedAuditType] = useState('internal')
  const [auditTitle, setAuditTitle] = useState('')
  const [isInviteHovered, setIsInviteHovered] = useState(false)
  const [existingAuditTitles, setExistingAuditTitles] = useState<string[]>([])
  const [showProcessingMessage, setShowProcessingMessage] = useState(false)

  useEffect(() => {
    const unsubscribe = getActiveFramworks()
    return () => unsubscribe && unsubscribe()
  }, [currentUser])

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (currentUser?.tenantId) {
        const tenantRef = doc(db, 'tenants', currentUser.tenantId)
        const companyRef = doc(tenantRef, 'settings', 'company')
        const docSnap = await getDoc(companyRef)

        if (docSnap.exists()) {
          const companyData = docSnap.data()
          setOrganization(companyData.legalName || '')
        }
      }
    }

    fetchCompanyData()
  }, [currentUser])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) return
    const tenantRef = doc(db, 'tenants', tenantId)
    const auditsRef = collection(tenantRef, 'audits')
    const unsubscribe = onSnapshot(auditsRef, (snapshot) => {
      const titles = snapshot.docs
        .map((doc) => doc.data().auditTitle?.trim().toLowerCase())
        .filter(Boolean)
      setExistingAuditTitles(titles)
    })
    return unsubscribe
  }, [currentUser])

  const getActiveFramworks = () => {
    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)
    const testsCollectionRef = collection(tenantRef, 'active_frameworks')

    const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
      const frameworksData = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
        frameworks: doc.data().frameworks,
      }))
      const frameworks = frameworksData[0]?.frameworks || []
      setActiveFrameworks(frameworks)
    })

    return unsubscribe
  }

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'employees')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      const data = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          role: doc.data().role,
          availability: doc.data().availability,
        }))
        .filter((item) => item.role === 'Auditor')
      setOwners(data)
    })

    return unsubscribe
  }, [])

  const handleFrameworkChange = (frameworks: string[]) => {
    setFrameworks(frameworks)

    if (frameworks.length === 0) {
      setFormErrors((prev) => ({...prev, frameworks: 'Please select at least one framework.'}))
    } else {
      setFormErrors((prev) => ({...prev, frameworks: ''}))
    }
  }

  const handleExportToDataroom = async () => {
    let errors = {...formErrors}
    let hasError = false

    if (frameworks.length === 0) {
      errors.frameworks = 'Please select a framework.'
      hasError = true
    } else {
      errors.frameworks = ''
    }

    if (selectedAuditType === 'internal') {
      if (!owner.trim()) {
        errors.owner = 'Please select an Auditor.'
        hasError = true
      } else {
        errors.owner = ''
      }
    }

    if (!auditTitle.trim()) {
      errors.auditTitle = 'Please enter an audit title.'
      hasError = true
    } else {
      errors.auditTitle = ''
    }

    if (!startDate.trim()) {
      errors.startDate = 'Please select an Auditor.'
      hasError = true
    } else {
      errors.startDate = ''
    }

    if (!endDate.trim()) {
      errors.endDate = 'Please select an Auditor.'
      hasError = true
    } else {
      errors.endDate = ''
    }

    if (
      existingAuditTitles
        .map((t) => t.trim().toLowerCase())
        .includes(auditTitle.trim().toLowerCase())
    ) {
      setFormErrors((prev) => ({
        ...prev,
        auditTitle: 'This audit title is already used. Please choose another.',
      }))
      return
    }

    setFormErrors(errors)

    if (hasError) {
      return
    }

    setShowProcessingMessage(true)
    setIsLoading(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const dataRoomRef = collection(tenantRef, 'dataroom')

      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = String(currentDate.getMonth() + 1).padStart(2, '0')
      const day = String(currentDate.getDate()).padStart(2, '0')
      const formattedDate = `${year}-${month}-${day}`
      const todaysDate = `${month}/${day}/${year}`

      const selectedOwner = owners.find((o) => o.uid === owner)

      for (const framework of frameworks) {
        const auditFolderName = auditTitle ? auditTitle : `${formattedDate}-${framework}-Audit`

        try {
          const auditFolderDoc = await addDoc(dataRoomRef, {
            fName: auditFolderName,
            folder_name: auditFolderName,
            name: auditFolderName,
            subFolder: `tenants/${tenantId}/dataroom/${auditFolderName}/`,
            time_created: formattedDate,
            tenantId: tenantId,
            uid: currentUser?.uid,
            is_folder: true,
            uploadedBy: currentUser?.userName,
            contentType: 'folder',
            fileId: '',
          })

          const folderId = auditFolderDoc.id

          const tenantRef2 = doc(db, 'tenants', tenantId)
          const policyRef = collection(tenantRef2, 'audits')

          const audits = await addDoc(policyRef, {
            createdAt: new Date().toISOString(),
            auditStartDate: formattedDate,
            framework: [framework],
            tenantId: tenantId,
            uid: currentUser?.uid,
            start_date: startDate,
            end_date: endDate,
            uploadedBy: currentUser?.userName,
            auditor: selectedAuditType === 'internal'
              ? [
                  {
                    fullName: selectedOwner?.full_name,
                    email: selectedOwner?.email,
                    id: selectedOwner?.id,
                  }
                ]
              : null,
            auditorOrganization: selectedOwner?.organization || '',
            status: 'Ongoing',
            organization: organization,
            dataroomFolderID: folderId,
            auditEvidenceFolderName: auditFolderName,
            auditType: selectedAuditType,
            auditTitle: auditTitle,
          })

          const uniqueID = audits.id

          await updateDoc(audits, {auditId: uniqueID})
          await updateDoc(auditFolderDoc, {fileId: folderId, auditID: audits.id})

          if (selectedAuditType === 'internal' && selectedOwner) {
            const employeeDocRef = doc(tenantRef2, 'employees', selectedOwner?.id)
            await updateDoc(employeeDocRef, {availability: false})
          }

          try {
            await ExportEmployee(currentUser, auditFolderName, folderId, formattedDate, uniqueID)

            await ExportRisk(currentUser, auditFolderName, folderId, formattedDate, uniqueID)

            await ExportVendor(currentUser, auditFolderName, folderId, formattedDate, uniqueID)

            await ExportAsset(currentUser, auditFolderName, folderId, formattedDate, uniqueID)

            await ExportZipTest(
              currentUser,
              [framework],
              auditFolderName,
              folderId,
              formattedDate,
              uniqueID
            )

            await ExportControl(
              currentUser,
              framework,
              auditFolderName,
              folderId,
              formattedDate,
              uniqueID
            )

            await ExportTest(
              currentUser,
              framework,
              auditFolderName,
              folderId,
              formattedDate,
              uniqueID
            )

            await ExportZipPolicy(
              currentUser,
              framework,
              auditFolderName,
              folderId,
              formattedDate,
              uniqueID
            )
          } catch (exportError) {
            throw exportError
          }
        } catch (frameworkError) {
          throw frameworkError
        }
      }

      const activitiesRef = collection(tenantRef, 'activities')
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} created a new ${
          selectedAuditType === 'internal' ? 'Internal' : 'External'
        } Audit "${auditTitle}" for ${frameworks} framework${
          selectedAuditType === 'internal'
            ? ` and assigned to auditor ${selectedOwner?.full_name}`
            : ''
        }`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        username: currentUser?.userName,
        action: 'Audit created',
        target: 'Audits',
        email: currentUser?.email,
        date: todaysDate,
      })

      showSuccessToast({message: 'Audit created successfully'})

      onClose()
    } catch (error) {
      showErrorToast({message: 'Error exporting to dataroom'})
    } finally {
      setIsLoading(false)
      setFrameworks([])
      setOwner('')
      setStartDate('')
      setEndDate('')
      setFormErrors({})
      setShowProcessingMessage(false)
    }
  }

  const handleOwnerChange = (selectedOwnerId: string) => {
    const selectedOwner = owners.find((owner) => owner.uid === selectedOwnerId)
    if (selectedOwner) {
      if (!selectedOwner.uid) {
        setFormErrors((prev) => ({
          ...prev,
          owner: 'Selected auditor has not accepted your invite.',
        }))
        setOwner('')
      } else if (!selectedOwner.availability) {
        setFormErrors((prev) => ({
          ...prev,
          owner: 'This auditor is already assigned to an audit and is not available',
        }))
      } else {
        setOwner(selectedOwnerId)
        setFormErrors((prev) => ({...prev, owner: ''}))
      }
    } else {
      setFormErrors((prev) => ({...prev, owner: 'Please select an Auditor.'}))
      setOwner('')
    }
  }

  return (
    <Drawer open={isOpen} onClose={onClose} direction='right' className='' size='45%'>
      <DrawerHeader title='Add Audit' onClose={onClose} />
      {showProcessingMessage && (
        <div
        className='card mx-6 mt-6'
        style={{backgroundColor: '#F3FAF7', border: '1.5px solid #0E9F6E'}}
      >
        <div className='px-6 py-4 fs-6 d-flex align-items-center'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='28'
            height='28'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            stroke-width='2'
            stroke-linecap='round'
            stroke-linejoin='round'
            className='icon icon-tabler icons-tabler-outline icon-tabler-info-circle me-5'
          >
            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
            <path d='M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0' />
            <path d='M12 9h.01' />
            <path d='M11 12h1v4h1' />
          </svg>
          You may continue with other tasks. All audit data will be uploaded to the Data Room, and
          you will be notified upon completion.
        </div>
      </div>
      )}
      <div
        className='scroll-y overflow-auto p-6 mb-4'
        tabIndex={-1}
        style={{height: 'calc(100vh - 120px)'}}
      >
        <div className='form-group w-100 mb-4'>
          <div className='mb-2'>
            <label className='text-dark fw-semibold fs-6  required'> Select Framework</label>
          </div>

          <select
            className={`form-select mb-2 ${formErrors.frameworks ? 'is-invalid' : ''}`}
            value={frameworks}
            onChange={(e) => {
              const selectedOptions = Array.from(e.target.selectedOptions, (option) => option.value)
              handleFrameworkChange(selectedOptions)
            }}
          >
            <option value='' selected>
              Select framework
            </option>
            {activeFrameworks.map((framework) => (
              <option key={framework} value={framework}>
                {framework}
              </option>
            ))}
          </select>
          {formErrors.frameworks && <span className='text-danger'>{formErrors.frameworks}</span>}
        </div>

        <div className='form-group mb-4'>
          <Input
            type='text'
            label='Audit Title'
            value={auditTitle}
            placeholder='Audit Title'
            maxLength={60}
            onChange={(e) => {
              const value = e.target.value
              setAuditTitle(value)
              if (existingAuditTitles.includes(value.trim().toLowerCase())) {
                setFormErrors((prev) => ({
                  ...prev,
                  auditTitle: 'This audit title is already used. Please choose another.',
                }))
              } else {
                setFormErrors((prev) => ({...prev, auditTitle: ''}))
              }
            }}
            error={formErrors.auditTitle}
            required
          />
          <div className='d-flex justify-content-between align-items-center mt-1'>
            {auditTitle.length > 40 && (
              <small className='text-warning'>
                {auditTitle.length >= 60
                  ? 'Character limit reached'
                  : 'Approaching character limit'}
              </small>
            )}
          </div>
        </div>

        <div className='form-group mb-4'>
          <label className='text-dark fw-semibold my-2 fs-6  required'>Perform Audit via</label>

          <div className='d-flex gap-3'>
            <div
              className='flex-fill cursor-pointer'
              style={{
                backgroundColor: selectedAuditType === 'internal' ? '#F9FAFB' : 'white',
                border: selectedAuditType === 'internal' ? '1px solid black' : '1px solid #E5E5E5',
                borderRadius: '6px',
                padding: '8px 16px',
              }}
              onClick={() => setSelectedAuditType('internal')}
            >
              <div
                className='d-flex justify-content-center align-items-center'
                style={{gap: '6px'}}
              >
                <span className='text-dark fw-semibold fs-6'>SecureSlate</span>
              </div>
            </div>

            <div
              className='flex-fill cursor-pointer'
              style={{
                backgroundColor: selectedAuditType === 'external' ? '#F9FAFB' : 'white',
                border: selectedAuditType === 'external' ? '1px solid black' : '1px solid #E5E5E5',
                borderRadius: '8px',
                padding: '8px 16px',
              }}
              onClick={() => setSelectedAuditType('external')}
            >
              <div
                className='d-flex justify-content-center align-items-center'
                style={{gap: '6px'}}
              >
                <span className='text-dark fw-semibold fs-6'>Sharing Externally</span>
              </div>
            </div>
          </div>
        </div>
        {selectedAuditType === 'internal' && (
          <div className='card p-6 form-group mb-4 mt-6  bg-gray-100'>
            <label className='text-dark fw-semibold mb-2 fs-6  required'>Auditor</label>
            <p className='fs-6'>
              Please select an auditor responsible for this audit. Not seeing your auditor in the
              list below?{' '}
              <Link
                to='/company-settings/users/auditors?open=true'
                style={{
                  color: isInviteHovered ? '#0E9F6E' : '#000000',
                  textDecoration: 'underline',
                  fontWeight: '600',
                  transition: 'color 0.2s',
                  cursor: 'pointer',
                }}
                onMouseEnter={() => setIsInviteHovered(true)}
                onMouseLeave={() => setIsInviteHovered(false)}
              >
                Invite Auditor
              </Link>
            </p>
            <select
              name='group'
              className={`form-select mb-2 ${formErrors.owner ? 'is-invalid' : ''}`}
              data-control='select2'
              data-placeholder='Select Owner'
              value={owner}
              onChange={(e) => handleOwnerChange(e.target.value)}
            >
              <option value='' selected>
                Select Auditor
              </option>
              {owners.map((owner) => (
                <option key={owner.id} value={owner.uid}>
                  {owner.full_name}
                </option>
              ))}
            </select>
            {formErrors.owner && <span className='text-danger'>{formErrors.owner}</span>}
          </div>
        )}
        <div className='fv-row mb-4'>
          <Input
            type='date'
            label='Start Date'
            value={startDate}
            placeholder='Select start date'
            onChange={(e) => {
              setStartDate(e.target.value)
              setFormErrors((prev) => ({...prev, startDate: ''}))
            }}
            error={formErrors.startDate}
            required
          />
        </div>
        <div className='fv-row mb-4'>
          <Input
            type='date'
            label='End Date'
            value={endDate}
            placeholder='Select end date'
            onChange={(e) => {
              setEndDate(e.target.value)
              setFormErrors((prev) => ({...prev, endDate: ''}))
            }}
            error={formErrors.endDate}
            required
          />
        </div>
      </div>
      <div
        className='offcanvas-footer d-flex justify-content-end  border-top py-4 px-6'
        style={{position: 'sticky', bottom: 0, backgroundColor: 'white'}}
      >
        <button
          className='btn btn-sm btn-light fs-6 me-4'
          onClick={() => {
            setFrameworks([])
            onClose()
            setFormErrors({})
          }}
          style={{
            height: '40px',
            borderRadius: '4px',
            transition: 'background-color 0.3s',
          }}
        >
          Cancel
        </button>
        {isLoading ? (
          <button
            className='btn btn-sm fs-6'
            disabled
            style={{
              backgroundColor: '#0E9F6E',
              color: '#fff',
              height: '40px',
              borderRadius: '4px',
              transition: 'background-color 0.3s',
            }}
          >
            Adding... <span className='spinner-border spinner-border-sm align-middle'></span>
          </button>
        ) : (
          <button
            className='btn btn-sm fs-6'
            onClick={handleExportToDataroom}
            onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
              (e.currentTarget.style.backgroundColor = '#148363')
            }
            onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
              (e.currentTarget.style.backgroundColor = '#0E9F6E')
            }
            style={{
              backgroundColor: '#0E9F6E',
              color: '#fff',
              height: '40px',
              borderRadius: '4px',
              transition: 'background-color 0.3s',
            }}
          >
            Add Audit
          </button>
        )}
      </div>
    </Drawer>
  )
}

export default AddAudit
